import { Component, computed, inject, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ErrorMeasuresDto } from '@api-types/dtos/create-analysis-configuration.dto';
import { AnalysisCalculationLevel } from '@api-types/enums/analysis-calculation-level.enum';
import { Measure } from '@api-types/enums/measure.enum';
import { ControlsOf } from '@api-types/helper/helper';
import {
  FormInputCheckboxComponent,
  H3Component,
  PmSelectionCardComponent,
  PmTabsGroupComponent,
} from '@libs/ui';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CreateAnalysisConfigurationBaseStepComponent } from 'demand-forecasting/analysis/components/configuration/steps/base-create-analysis-configuration-step-component.interface';
import {
  ACCURACY_MEASURES,
  accuracyKeyOf,
  createMeasureVisibility,
  getAccuracyColumnHeader,
  getMeasureColumnHeader,
  MEASURE_CATEGORIES,
  RELATIVE_CATEGORY_LABEL_KEY,
} from 'demand-forecasting/analysis/services/measure-display';

type MeasureVariantTab = 'error' | 'accuracy';

type MeasureControlName = Exclude<keyof ErrorMeasuresDto, 'calculationLevel'>;

type MeasureCheckbox = {
  controlName: MeasureControlName;
  label: string;
};

type MeasureCheckboxCategory = {
  labelKey: string;
  checkboxes: MeasureCheckbox[];
};

type MeasurePanel = {
  tab: MeasureVariantTab;
  categories: MeasureCheckboxCategory[];
};

@Component({
  selector: 'pm-error-measures-step-analysis-configuration',
  templateUrl: './error-measures-step-analysis-configuration.component.html',
  imports: [
    ReactiveFormsModule,
    FormInputCheckboxComponent,
    H3Component,
    TranslateModule,
    PmSelectionCardComponent,
    PmTabsGroupComponent,
  ],
})
export class ErrorMeasuresStepAnalysisConfigurationComponent extends CreateAnalysisConfigurationBaseStepComponent<ErrorMeasuresDto> {
  private readonly store = inject(Store);
  private readonly translateService = inject(TranslateService);

  private readonly isMeasureVisible = createMeasureVisibility(this.store);

  private readonly measureControls = [
    ...Object.values(Measure),
    ...ACCURACY_MEASURES.map(accuracyKeyOf),
  ].reduce(
    (acc, controlName) => {
      const key = controlName as MeasureControlName;
      acc[key] = this.fb.control<boolean>(false, { nonNullable: true });
      return acc;
    },
    {} as ControlsOf<Omit<ErrorMeasuresDto, 'calculationLevel'>>,
  );

  form = this.fb.group({
    ...this.measureControls,
    calculationLevel: this.fb.control<AnalysisCalculationLevel>(
      AnalysisCalculationLevel.HIGHEST,
      { nonNullable: true },
    ),
  });

  protected readonly calculationLevels = [
    {
      value: AnalysisCalculationLevel.HIGHEST,
      titleKey: 'ANALYSIS.CALCULATION_LEVEL.HIGHEST',
      descriptionKey: 'ANALYSIS.CALCULATION_LEVEL.HIGHEST_DESCRIPTION',
    },
    {
      value: AnalysisCalculationLevel.LOWEST,
      titleKey: 'ANALYSIS.CALCULATION_LEVEL.LOWEST',
      descriptionKey: 'ANALYSIS.CALCULATION_LEVEL.LOWEST_DESCRIPTION',
    },
  ];

  protected readonly selectedTab = signal<MeasureVariantTab>('error');

  protected readonly variantTabs = [
    {
      value: 'error' satisfies MeasureVariantTab,
      label: 'ANALYSIS.CREATE_CONFIGURATION.MEASURE_VARIANT_ERROR',
    },
    {
      value: 'accuracy' satisfies MeasureVariantTab,
      label: 'ANALYSIS.CREATE_CONFIGURATION.MEASURE_VARIANT_ACCURACY',
    },
  ];

  /**
   * both variants stay in the layout so the dialog keeps its height when the
   * shorter accuracy list is shown.
   */
  protected readonly measurePanels = computed((): MeasurePanel[] => [
    { tab: 'error', categories: this.errorCategories() },
    { tab: 'accuracy', categories: this.accuracyCategories() },
  ]);

  protected onTabChange(tab: string): void {
    this.selectedTab.set(tab as MeasureVariantTab);
  }

  protected onCalculationLevelChange(level: AnalysisCalculationLevel): void {
    this.form.controls.calculationLevel.setValue(level);
  }

  private errorCategories(): MeasureCheckboxCategory[] {
    return MEASURE_CATEGORIES.map((category) => ({
      labelKey: category.labelKey,
      checkboxes: category.measures
        .filter((measure) => this.isMeasureVisible(measure))
        .map((measure) => ({
          controlName: measure as MeasureControlName,
          label: getMeasureColumnHeader(measure),
        })),
    })).filter((category) => category.checkboxes.length > 0);
  }

  private accuracyCategories(): MeasureCheckboxCategory[] {
    const accuracySuffix = this.translateService.instant(
      'ANALYSIS.ACCURACY_SUFFIX',
    );
    const checkboxes = ACCURACY_MEASURES.filter((measure) =>
      this.isMeasureVisible(measure),
    ).map((measure) => ({
      controlName: accuracyKeyOf(measure) as MeasureControlName,
      label: getAccuracyColumnHeader(measure, accuracySuffix),
    }));

    return checkboxes.length > 0
      ? [{ labelKey: RELATIVE_CATEGORY_LABEL_KEY, checkboxes }]
      : [];
  }
}
